import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { z } from "zod";
import { Camera, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { publishedServicePages } from "@/config/services";
import { useI18n } from "@/i18n";
import { localizeServicePage } from "@/i18n/content";
import { intakeCopy } from "@/i18n/intake-copy";
import { localizedPath } from "@/i18n/route-manifest";
import { initializeAnalytics, trackAnalyticsEvent } from "@/lib/analytics";
import {
  captureAttribution,
  isLeadTransportConfigured,
  LeadTransportUnavailableError,
  submitLead,
} from "@/lib/leads";
import { photoFollowUpNotice, selectedPhotos, structuredMessage } from "@/lib/intake";
import { IntakeField, IntakeSelect, type IntakeStatus } from "./intake-form-shared";

export function PlannedEstimateForm() {
  const { locale } = useI18n();
  const copy = intakeCopy(locale);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [status, setStatus] = useState<IntakeStatus>(
    isLeadTransportConfigured() ? "idle" : "unavailable",
  );
  const started = useRef(false);

  useEffect(() => {
    initializeAnalytics();
    captureAttribution();
  }, []);

  function trackStart() {
    if (started.current) return;
    started.current = true;
    trackAnalyticsEvent({
      event: "estimate_started",
      form_id: "planned_estimate",
      lead_type: "planned",
    });
  }

  function onPhotos(event: ChangeEvent<HTMLInputElement>) {
    const next = selectedPhotos(event);
    setPhotos(next);
    if (next.length) {
      trackAnalyticsEvent({
        event: "photo_uploaded",
        form_id: "planned_estimate",
        lead_type: "planned",
        file_count: String(next.length),
        file_status: "selected_not_transmitted",
      });
    }
  }

  const schema = z.object({
    propertyType: z.string().min(1, copy.planned.errors.propertyType),
    address: z.string().trim().min(3, copy.planned.errors.address).max(200),
    city: z.string().trim().min(1, copy.planned.errors.city).max(100),
    postalCode: z.string().trim().min(3, copy.planned.errors.postalCode).max(12),
    service: z.string().min(1, copy.planned.errors.service),
    approximateSize: z.string().trim().max(60).optional().default(""),
    stories: z.string().min(1, copy.planned.errors.option),
    slope: z.string().trim().max(40).optional().default(""),
    skylights: z.string().trim().max(40).optional().default(""),
    complexity: z.string().trim().max(40).optional().default(""),
    existingRoof: z.string().min(1, copy.planned.errors.option),
    roofAge: z.string().trim().max(60).optional().default(""),
    desiredSystem: z.string().trim().max(100).optional().default(""),
    knownIssues: z.string().trim().max(1500).optional().default(""),
    timeline: z.string().min(1, copy.planned.errors.timeline),
    name: z.string().trim().min(1, copy.planned.errors.name).max(100),
    phone: z
      .string()
      .trim()
      .min(7, copy.planned.errors.phone)
      .max(30)
      .regex(/^[0-9+()\-.\s]+$/, copy.planned.errors.phone),
    email: z.string().trim().email(copy.planned.errors.email).max(255),
    preferredContact: z.string().min(1, copy.planned.errors.contact),
  });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const parsed = schema.safeParse(Object.fromEntries(new FormData(form).entries()));
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setErrors(next);
      form.querySelector<HTMLElement>(`[name="${Object.keys(next)[0]}"]`)?.focus();
      return;
    }

    setErrors({});
    setStatus("sending");
    const data = parsed.data;
    try {
      await submitLead({
        name: data.name,
        phone: data.phone,
        email: data.email,
        postalCode: data.postalCode,
        roofingNeed: `lead_type=planned | ${data.service} | property_type=${data.propertyType}`,
        timeline: data.timeline,
        message: structuredMessage(
          "lead_type=planned",
          [
            ["Property type", data.propertyType],
            ["Address", data.address],
            ["City", data.city],
            ["Service", data.service],
            ["Approximate roof size", data.approximateSize],
            ["Stories", data.stories],
            ["Slope", data.slope],
            ["Skylights", data.skylights],
            ["Complexity", data.complexity],
            ["Existing roof", data.existingRoof],
            ["Approximate roof age", data.roofAge],
            ["Desired system", data.desiredSystem],
            ["Preferred contact", data.preferredContact],
          ],
          data.knownIssues,
          photos.length,
        ),
        locale,
      });
      trackAnalyticsEvent({
        event: "estimate_completed",
        form_id: "planned_estimate",
        lead_type: "planned",
        property_type: data.propertyType,
      });
      form.reset();
      setPhotos([]);
      window.location.assign(localizedPath("core:thank-you", locale));
    } catch (error) {
      setStatus(error instanceof LeadTransportUnavailableError ? "unavailable" : "error");
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      onFocusCapture={trackStart}
      onChange={trackStart}
      noValidate
      className="rounded-xl border border-border bg-card p-6 shadow-card sm:p-8"
    >
      <div className="mb-7 flex items-start gap-3 border-b border-border pb-6">
        <ClipboardCheck className="mt-0.5 size-5 shrink-0 text-accent" aria-hidden="true" />
        <p className="text-sm leading-6 text-muted-foreground">{copy.planned.intro}</p>
      </div>

      <fieldset>
        <legend className="mb-4 font-display text-xl">{copy.planned.propertyLegend}</legend>
        <div className="grid gap-5 sm:grid-cols-2">
          <IntakeField
            id="propertyType"
            label={copy.planned.propertyType}
            error={errors["propertyType"]}
          >
            <IntakeSelect
              id="propertyType"
              options={copy.planned.propertyTypes}
              error={errors["propertyType"]}
            />
          </IntakeField>
          <IntakeField id="city" label={copy.planned.city} error={errors["city"]}>
            <Input
              id="city"
              name="city"
              autoComplete="address-level2"
              required
              aria-invalid={!!errors["city"]}
              aria-describedby={errors["city"] ? "city-error" : undefined}
            />
          </IntakeField>
          <IntakeField id="address" label={copy.planned.address} error={errors["address"]}>
            <Input
              id="address"
              name="address"
              autoComplete="street-address"
              required
              aria-invalid={!!errors["address"]}
              aria-describedby={errors["address"] ? "address-error" : undefined}
            />
          </IntakeField>
          <IntakeField id="postalCode" label={copy.planned.postalCode} error={errors["postalCode"]}>
            <Input
              id="postalCode"
              name="postalCode"
              autoComplete="postal-code"
              required
              aria-invalid={!!errors["postalCode"]}
              aria-describedby={errors["postalCode"] ? "postalCode-error" : undefined}
            />
          </IntakeField>
        </div>
      </fieldset>

      <fieldset className="mt-9 border-t border-border pt-7">
        <legend className="mb-4 font-display text-xl">{copy.planned.roofLegend}</legend>
        <div className="grid gap-5 sm:grid-cols-2">
          <IntakeField
            id="service"
            label={copy.planned.service}
            error={errors["service"]}
            className="sm:col-span-2"
          >
            <IntakeSelect
              id="service"
              error={errors["service"]}
              options={publishedServicePages.map((service) => ({
                value: service.slug,
                label: localizeServicePage(service, locale).title,
              }))}
            />
          </IntakeField>
          <IntakeField id="approximateSize" label={copy.planned.approximateSize}>
            <Input
              id="approximateSize"
              name="approximateSize"
              placeholder={copy.planned.sizePlaceholder}
            />
          </IntakeField>
          <IntakeField id="stories" label={copy.planned.stories} error={errors["stories"]}>
            <IntakeSelect
              id="stories"
              options={copy.planned.storiesOptions}
              error={errors["stories"]}
            />
          </IntakeField>
          <IntakeField id="slope" label={copy.planned.slope}>
            <OptionalIntakeSelect id="slope" options={copy.planned.slopeOptions} />
          </IntakeField>
          <IntakeField id="skylights" label={copy.planned.skylights}>
            <OptionalIntakeSelect id="skylights" options={copy.planned.skylightOptions} />
          </IntakeField>
          <IntakeField id="complexity" label={copy.planned.complexity}>
            <OptionalIntakeSelect id="complexity" options={copy.planned.complexityOptions} />
          </IntakeField>
          <IntakeField
            id="existingRoof"
            label={copy.planned.existingRoof}
            error={errors["existingRoof"]}
          >
            <IntakeSelect
              id="existingRoof"
              options={copy.planned.roofTypes}
              error={errors["existingRoof"]}
            />
          </IntakeField>
          <IntakeField id="roofAge" label={copy.planned.roofAge}>
            <Input id="roofAge" name="roofAge" placeholder={copy.planned.agePlaceholder} />
          </IntakeField>
          <IntakeField
            id="desiredSystem"
            label={copy.planned.desiredSystem}
            className="sm:col-span-2"
          >
            <Input id="desiredSystem" name="desiredSystem" />
          </IntakeField>
          <IntakeField id="knownIssues" label={copy.planned.knownIssues} className="sm:col-span-2">
            <Textarea id="knownIssues" name="knownIssues" rows={4} />
          </IntakeField>
          <IntakeField id="timeline" label={copy.planned.timeline} error={errors["timeline"]}>
            <IntakeSelect
              id="timeline"
              options={copy.planned.timelines}
              error={errors["timeline"]}
            />
          </IntakeField>
          <IntakeField id="planned-photos" label={copy.planned.photos} className="sm:col-span-2">
            <Input
              id="planned-photos"
              name="photos"
              type="file"
              accept="image/*"
              multiple
              onChange={onPhotos}
            />
            <p className="mt-2 flex items-start gap-2 text-xs leading-5 text-muted-foreground">
              <Camera className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              {photoFollowUpNotice(photos.length, {
                empty: copy.common.photoEmpty,
                selected: copy.common.photoSelected,
              })}
            </p>
          </IntakeField>
        </div>
      </fieldset>

      <fieldset className="mt-9 border-t border-border pt-7">
        <legend className="mb-4 font-display text-xl">{copy.planned.contactLegend}</legend>
        <div className="grid gap-5 sm:grid-cols-2">
          <IntakeField id="planned-name" label={copy.planned.name} error={errors["name"]}>
            <Input
              id="planned-name"
              name="name"
              autoComplete="name"
              required
              aria-invalid={!!errors["name"]}
              aria-describedby={errors["name"] ? "planned-name-error" : undefined}
            />
          </IntakeField>
          <IntakeField id="planned-phone" label={copy.planned.phone} error={errors["phone"]}>
            <Input
              id="planned-phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              required
              aria-invalid={!!errors["phone"]}
              aria-describedby={errors["phone"] ? "planned-phone-error" : undefined}
            />
          </IntakeField>
          <IntakeField id="planned-email" label={copy.planned.email} error={errors["email"]}>
            <Input
              id="planned-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              aria-invalid={!!errors["email"]}
              aria-describedby={errors["email"] ? "planned-email-error" : undefined}
            />
          </IntakeField>
          <IntakeField
            id="preferredContact"
            label={copy.planned.preferredContact}
            error={errors["preferredContact"]}
          >
            <IntakeSelect
              id="preferredContact"
              options={copy.planned.contactMethods}
              error={errors["preferredContact"]}
            />
          </IntakeField>
        </div>
      </fieldset>

      {status === "error" || status === "unavailable" ? (
        <p role="alert" className="mt-5 text-sm text-destructive">
          {status === "unavailable" ? copy.common.unavailable : copy.common.sendError}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="brand"
        size="xl"
        className="mt-7 w-full"
        disabled={status === "sending" || status === "unavailable"}
      >
        {status === "sending" ? copy.common.sending : copy.planned.submit}
      </Button>
    </form>
  );
}

function OptionalIntakeSelect({
  id,
  options,
}: {
  id: string;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <select
      id={id}
      name={id}
      defaultValue=""
      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
    >
      <option value="">-</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
